import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Grid,
  Divider,
  Autocomplete,
  TextField,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon } from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { feeCategoryService, classService } from '../../services/apiService';
import { API_ENDPOINTS } from '../../config/api';
import { showToast } from '../../utils/toast';

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const FeeStatus = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawerWidth = 280;

  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const [categories, setCategories] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch fee categories and classes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, classesRes] = await Promise.all([
          feeCategoryService.getAll({ is_active: 1 }),
          classService.getAll(),
        ]);
        const categoriesData = categoriesRes?.data || categoriesRes || [];
        const classesData = classesRes?.data || classesRes || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        setClasses(Array.isArray(classesData) ? classesData : []);
      } catch (err) {
        console.error('Error fetching data:', err);
        showToast.error('Failed to load data');
      }
    };
    fetchData();
  }, []);

  // Fetch fee status
  const fetchFeeStatus = useCallback(async () => {
    if (!selectedCategory) {
      setStatusData(null);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        fee_category_id: selectedCategory,
      });

      const category = categories.find(c => c.id === selectedCategory);
      if (category?.frequency === 'monthly') {
        if (selectedMonth) {
          params.append('billing_month', selectedMonth);
        } else {
          setError('Please select a month for monthly fee category');
          setLoading(false);
          return;
        }
      }

      if (selectedClass) {
        params.append('class_id', selectedClass);
      }

      if (selectedStatus) {
        params.append('status', selectedStatus);
      }

      const response = await fetch(`${API_ENDPOINTS.FEE_STATUS}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setStatusData(data.data);
      } else {
        setError(data.error || 'Failed to fetch fee status');
        setStatusData(null);
      }
    } catch (err) {
      console.error('Error fetching fee status:', err);
      setError('Failed to fetch fee status');
      setStatusData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, selectedClass, selectedMonth, selectedStatus, categories]);

  useEffect(() => {
    fetchFeeStatus();
  }, [fetchFeeStatus]);

  const selectedCategoryData = useMemo(() => {
    return categories.find(c => c.id === selectedCategory);
  }, [categories, selectedCategory]);

  const isMonthly = selectedCategoryData?.frequency === 'monthly';

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setSelectedMonth('');
    setSelectedStatus('');
    setSelectedClass('');
    setStatusData(null);
  };

  const handleClassChange = (e) => {
    setSelectedClass(e.target.value);
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                mb: 3,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Fee Status
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View fee payment status for all students by category
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', md: 'flex-end' }}
                >
                  <FormControl size="small" sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}>
                    <InputLabel>Fee Category</InputLabel>
                    <Select
                      value={selectedCategory}
                      onChange={handleCategoryChange}
                      label="Fee Category"
                    >
                      <MenuItem value="">
                        <em>Select Category</em>
                      </MenuItem>
                      {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                          {cat.name} ({cat.frequency})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {isMonthly && (
                    <FormControl size="small" sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}>
                      <InputLabel>Month</InputLabel>
                      <Select
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        label="Month"
                      >
                        <MenuItem value="">
                          <em>Select Month</em>
                        </MenuItem>
                        {MONTH_OPTIONS.map((month) => (
                          <MenuItem key={month.value} value={month.value}>
                            {month.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  <FormControl size="small" sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      label="Status"
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="unpaid">Unpaid</MenuItem>
                    </Select>
                  </FormControl>

                  <Autocomplete
                    options={[{ id: '', name: 'All Classes', section: null }, ...classes]}
                    getOptionLabel={(option) => {
                      if (!option || option.id === '') return 'All Classes';
                      return `Class ${option.name}${option.section ? ` - ${option.section}` : ''}`;
                    }}
                    value={selectedClass ? classes.find((cls) => cls.id === selectedClass) || null : { id: '', name: 'All Classes', section: null }}
                    onChange={(event, newValue) => {
                      handleClassChange({ target: { value: newValue && newValue.id !== '' ? newValue.id : '' } });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Class"
                        placeholder="Search and select class..."
                        size="small"
                      />
                    )}
                    sx={{ flex: { xs: '1 1 100%', md: '1 1 0' }, minWidth: 0 }}
                    ListboxProps={{
                      style: {
                        maxHeight: 200,
                      },
                    }}
                    noOptionsText="No classes found"
                  />
                </Stack>
              </Stack>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : statusData ? (
              <>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Total Students
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                          {statusData.total_students}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Paid
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="success.main">
                          {statusData.paid_count}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Unpaid
                        </Typography>
                        <Typography variant="h4" fontWeight={700} color="error.main">
                          {statusData.unpaid_count}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Students Table/Cards */}
                {statusData.students.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      textAlign: 'center',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No students found matching the selected criteria.
                    </Typography>
                  </Paper>
                ) : isMobile ? (
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    {statusData.students.map((student) => (
                      <Card key={student.student_id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                              <Box>
                                <Typography variant="h6" fontWeight={600}>
                                  {student.student_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Roll No: {student.roll_number} • {student.class_name} {student.class_section}
                                </Typography>
                              </Box>
                              <Chip
                                icon={student.status === 'paid' ? <CheckCircleIcon /> : <CancelIcon />}
                                label={student.status === 'paid' ? 'Paid' : 'Unpaid'}
                                color={student.status === 'paid' ? 'success' : 'error'}
                                size="small"
                              />
                            </Box>
                            <Divider />
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">
                                Expected:
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {formatCurrency(student.expected_amount)}
                              </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">
                                Paid:
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="success.main">
                                {formatCurrency(student.total_paid)}
                              </Typography>
                            </Stack>
                            <Stack direction="row" justifyContent="space-between">
                              <Typography variant="body2" color="text.secondary">
                                Balance:
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="error.main">
                                {formatCurrency(student.balance)}
                              </Typography>
                            </Stack>
                            {student.last_payment_date && (
                              <Typography variant="caption" color="text.secondary">
                                Last Payment: {formatDate(student.last_payment_date)}
                              </Typography>
                            )}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Roll No</TableCell>
                          <TableCell>Student Name</TableCell>
                          <TableCell>Class</TableCell>
                          <TableCell align="right">Expected Amount</TableCell>
                          <TableCell align="right">Paid</TableCell>
                          <TableCell align="right">Balance</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell>Last Payment</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statusData.students.map((student) => (
                          <TableRow key={student.student_id} hover>
                            <TableCell>{student.roll_number}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {student.student_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {student.class_name} {student.class_section}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(student.expected_amount)}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="success.main" fontWeight={600}>
                                {formatCurrency(student.total_paid)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="error.main" fontWeight={600}>
                                {formatCurrency(student.balance)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={student.status === 'paid' ? <CheckCircleIcon /> : <CancelIcon />}
                                label={student.status === 'paid' ? 'Paid' : 'Unpaid'}
                                color={student.status === 'paid' ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {student.last_payment_date ? formatDate(student.last_payment_date) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Please select a fee category to view fee status.
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default FeeStatus;

